import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Lock, FileText, CalendarClock, MapPin, UserCircle2, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { useAuth, api, formatApiError, LOGO_URL } from "@/lib/api";
import PasswordStep from "@/components/formateur/onboarding/PasswordStep";
import PublicProfileForm from "@/components/formateur/PublicProfileForm";
import ProgrammeForm from "@/components/formateur/ProgrammeForm";
import DisponibilitesCalendar from "@/components/formateur/DisponibilitesCalendar";
import VillesForm from "@/components/formateur/VillesForm";
import FormationsTaughtForm from "@/components/formateur/FormationsTaughtForm";

// Canonical 6-step definition. Stable keys — never re-order dynamically.
const ALL_STEPS = [
    { key: "password", label: "Mot de passe", icon: Lock },
    { key: "profil", label: "Profil public", icon: UserCircle2 },
    { key: "formations", label: "Mes formations", icon: ListChecks },
    { key: "programme", label: "Programme", icon: FileText },
    { key: "disponibilites", label: "Disponibilités", icon: CalendarClock },
    { key: "villes", label: "Villes", icon: MapPin },
];

function computeNextStep({ mustChangePassword, hasPublicProfile, hasFormationsTaught, hasProgramme, disponibilitesCount, villesCount }) {
    if (mustChangePassword) return "password";
    if (!hasPublicProfile) return "profil";
    if (!hasFormationsTaught) return "formations";
    if (!hasProgramme) return "programme";
    if (!disponibilitesCount || disponibilitesCount === 0) return "disponibilites";
    if (!villesCount || villesCount === 0) return "villes";
    return "done";
}

export default function Onboarding() {
    const { user, refresh, logout } = useAuth();
    const navigate = useNavigate();

    const STEPS = ALL_STEPS;
    const [stepKey, setStepKey] = useState(null);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        if (!user) return;
        // If user is already fully onboarded, NEVER stay on the onboarding page —
        // even if their public profile is incomplete. The dashboard banner handles that.
        if (!user.must_change_password && user.onboarding_completed) {
            navigate("/formateur", { replace: true });
            return;
        }
        setLoading(true);
        api.get("/formateur/me")
            .then((r) => {
                if (!alive) return;
                setSummary(r.data);
                const next = computeNextStep({
                    mustChangePassword: !!user?.must_change_password,
                    hasPublicProfile: !!r.data?.has_public_profile,
                    hasFormationsTaught: (r.data?.formations_taught_count || 0) > 0,
                    hasProgramme: !!r.data?.has_programme,
                    disponibilitesCount: r.data?.disponibilites_count || 0,
                    villesCount: r.data?.villes_count || 0,
                });
                if (next === "done") {
                    navigate("/formateur", { replace: true });
                    return;
                }
                setStepKey(next);
            })
            .catch((err) => {
                if (!alive) return;
                toast.error(formatApiError(err));
            })
            .finally(() => alive && setLoading(false));
        return () => {
            alive = false;
        };
    }, [user?.id, user?.must_change_password, navigate]);

    const refreshSummary = async () => {
        try {
            const { data } = await api.get("/formateur/me");
            setSummary(data);
            return data;
        } catch {
            return null;
        }
    };

    const advance = async () => {
        const fresh = await refreshSummary();
        const u = await refresh();
        const next = computeNextStep({
            mustChangePassword: !!u?.must_change_password,
            hasPublicProfile: !!fresh?.has_public_profile,
            hasFormationsTaught: (fresh?.formations_taught_count || 0) > 0,
            hasProgramme: !!fresh?.has_programme,
            disponibilitesCount: fresh?.disponibilites_count || 0,
            villesCount: fresh?.villes_count || 0,
        });
        if (next === "done") {
            await finish();
            return;
        }
        setStepKey(next);
    };

    const finish = async () => {
        try {
            await api.post("/formateur/onboarding/complete");
            await refresh();
            toast.success("Onboarding terminé — bienvenue !");
            navigate("/formateur", { replace: true });
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const stepStatus = (key) => {
        if (!summary) return "pending";
        switch (key) {
            case "password":
                return user?.must_change_password ? "pending" : "done";
            case "profil":
                return summary.has_public_profile ? "done" : "pending";
            case "formations":
                return (summary.formations_taught_count || 0) > 0 ? "done" : "pending";
            case "programme":
                return summary.has_programme ? "done" : "pending";
            case "disponibilites":
                return (summary.disponibilites_count || 0) > 0 ? "done" : "pending";
            case "villes":
                return (summary.villes_count || 0) > 0 ? "done" : "pending";
            default:
                return "pending";
        }
    };
    const completedCount = STEPS.filter((s) => stepStatus(s.key) === "done").length;
    const progress = Math.round((completedCount / STEPS.length) * 100);

    if (loading || !stepKey) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#FAFAFA]" data-testid="onboarding-loading">
                <div className="text-sm text-brand-smoke">Chargement de votre espace…</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#FAFAFA]" data-testid="page-onboarding">
            <div className="sticky top-0 z-20 border-b border-brand-line bg-white/95 backdrop-blur">
                <div className="mx-auto flex max-w-6xl items-center justify-between gap-6 px-6 py-5">
                    <div className="flex items-center gap-3">
                        <img src={LOGO_URL} alt="Cursus Physio" className="h-9 w-auto" />
                        <div className="hidden border-l border-brand-line pl-4 text-sm text-brand-smoke sm:block">
                            Bienvenue <strong className="text-brand-ink">{user?.first_name}</strong>.
                            Configurons votre espace formateur.
                        </div>
                    </div>
                    <button
                        onClick={async () => {
                            await logout();
                            navigate("/login", { replace: true });
                        }}
                        data-testid="onboarding-logout"
                        className="text-xs text-brand-smoke hover:text-brand-ink"
                    >
                        Se déconnecter
                    </button>
                </div>
                <div className="relative h-1 w-full bg-brand-line">
                    <motion.div
                        className="absolute inset-y-0 left-0 bg-brand-orange"
                        initial={{ width: 0 }}
                        animate={{ width: `${progress}%` }}
                        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    />
                </div>
            </div>

            <div className="mx-auto max-w-6xl px-6 py-10">
                <div className="flex items-center justify-between gap-2 overflow-x-auto" data-testid="onboarding-stepper">
                    {STEPS.map((s, i) => {
                        const Icon = s.icon;
                        const status = stepStatus(s.key);
                        const done = status === "done";
                        const active = s.key === stepKey;
                        const clickable = done || active;
                        return (
                            <div key={s.key} className="flex flex-1 items-center min-w-0">
                                <button
                                    type="button"
                                    disabled={!clickable}
                                    onClick={() => clickable && setStepKey(s.key)}
                                    data-testid={`onboarding-step-label-${s.key}`}
                                    className={`flex flex-1 items-center gap-3 rounded-2xl px-3 py-3 text-left transition-all ${
                                        active
                                            ? "bg-white border border-brand-line shadow-premium"
                                            : done
                                              ? "opacity-85 hover:opacity-100"
                                              : "opacity-50 cursor-not-allowed"
                                    }`}
                                >
                                    <div
                                        className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full ${
                                            done
                                                ? "bg-green-600 text-white"
                                                : active
                                                  ? "bg-brand-orange text-white"
                                                  : "bg-brand-line text-brand-smoke"
                                        }`}
                                    >
                                        {done ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                                    </div>
                                    <div className="min-w-0">
                                        <div className="text-[10px] uppercase tracking-wider text-brand-smoke">
                                            Étape {i + 1}/{STEPS.length}
                                        </div>
                                        <div className="truncate text-sm font-semibold text-brand-ink">{s.label}</div>
                                    </div>
                                </button>
                                {i < STEPS.length - 1 && <div className="mx-1 hidden h-px flex-1 bg-brand-line md:block" />}
                            </div>
                        );
                    })}
                </div>

                <div className="mt-10" data-testid={`onboarding-content-${stepKey}`}>
                    {stepKey === "password" && <PasswordStep onDone={advance} />}
                    {stepKey === "profil" && <PublicProfileForm mode="onboarding" onSubmitted={advance} />}
                    {stepKey === "formations" && <FormationsTaughtForm mode="onboarding" onSubmitted={advance} />}
                    {stepKey === "programme" && <ProgrammeForm mode="onboarding" onSubmitted={advance} />}
                    {stepKey === "disponibilites" && <DisponibilitesCalendar mode="onboarding" onSubmitted={advance} />}
                    {stepKey === "villes" && <VillesForm mode="onboarding" onSubmitted={advance} />}
                </div>
            </div>
        </div>
    );
}
